import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { 
  Heart, 
  ArrowLeft, 
  Package, 
  Star, 
  Crown, 
  Check, 
  Sparkles,
  Shield,
  Zap,
  Calendar,
  Feather,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/i18n/LanguageContext';
import { useUserCredits } from '@/hooks/useUserCredits';
import { AuthenticatedHeader } from '@/components/layout/AuthenticatedHeader';
import { useFounderStatus } from '@/hooks/useFounderStatus';
import { useFounderOffer } from '@/hooks/useFounderOffer';
import { FounderPlanTransition } from '@/components/founder/FounderPlanTransition';
import { toast } from 'sonner';
import { 
  BUNDLE_CONFIGS, 
  CREDIT_PACK_CONFIGS, 
  type BundleType, 
  type CreditPackType 
} from '@/types/credits';

const bundleIcons: Record<BundleType, React.ComponentType<{ className?: string }>> = {
  starter: Package,
  connection: Star,
  legacy: Crown,
};

const bundleColors: Record<BundleType, { text: string; bg: string }> = {
  starter: { text: 'text-muted-foreground', bg: 'bg-muted/50' },
  connection: { text: 'text-primary', bg: 'bg-primary/10' },
  legacy: { text: 'text-amber-500', bg: 'bg-amber-500/10' },
};

const copy = {
  en: {
    pageTitle: 'Add More Moments', pageSubtitle: 'Continue expressing what matters', backToDashboard: 'Back to Dashboard', currentStatus: 'Your Current Status', creditsAvailable: 'Announcements Available', currentTier: 'Current Tier', freeTier: 'Free', featuresUnlocked: 'Features Unlocked', featureAi: 'AI Writing Assist', featureScheduling: 'Scheduling', featureRecurring: 'Recurring Announcements', featureTreslog: 'TRESLOG Access', featureKeepsakes: 'Downloadable Keepsakes', creditPacks: 'Credit Packs', creditPacksSubtitle: 'Add more announcements while keeping all your features', featuresStay: 'Your {tier} features remain unlocked forever', bundles: 'Get Started', bundlesSubtitle: 'Unlock features and add announcements', popular: 'Most Popular', oneTime: 'one-time', get: 'Get', upgradeSection: 'Want More Features?', upgradeSubtitle: 'Upgrade to unlock additional capabilities', upgradeTo: 'Upgrade to', refundNote: "Announcements are added instantly and never expire. If something doesn't feel right, reach out and we'll help.", comingSoon: 'Payment coming soon', limited: 'limited', unlimited: 'unlimited', announcements: 'announcements', bestValue: 'Best Value',
  },
  es: {
    pageTitle: 'Agregar Más Momentos', pageSubtitle: 'Continúa expresando lo que importa', backToDashboard: 'Volver al Panel', currentStatus: 'Tu Estado Actual', creditsAvailable: 'Anuncios Disponibles', currentTier: 'Nivel Actual', freeTier: 'Gratis', featuresUnlocked: 'Funciones Desbloqueadas', featureAi: 'Asistencia de IA', featureScheduling: 'Programación', featureRecurring: 'Anuncios Recurrentes', featureTreslog: 'Acceso a TRESLOG', featureKeepsakes: 'Recuerdos Descargables', creditPacks: 'Paquetes de Créditos', creditPacksSubtitle: 'Añade más anuncios manteniendo todas tus funciones', featuresStay: 'Tus funciones de {tier} permanecen desbloqueadas para siempre', bundles: 'Comenzar', bundlesSubtitle: 'Desbloquea funciones y añade anuncios', popular: 'Más Popular', oneTime: 'único pago', get: 'Obtener', upgradeSection: '¿Quieres Más Funciones?', upgradeSubtitle: 'Actualiza para desbloquear capacidades adicionales', upgradeTo: 'Actualizar a', refundNote: 'Los anuncios se añaden instantáneamente y nunca expiran. Si algo no se siente bien, contáctanos.', comingSoon: 'Pago próximamente', limited: 'limitado', unlimited: 'ilimitado', announcements: 'anuncios', bestValue: 'Mejor Valor',
  },
  fr: {
    pageTitle: 'Ajouter Plus de Moments', pageSubtitle: 'Continuez à exprimer ce qui compte', backToDashboard: 'Retour au Tableau de Bord', currentStatus: 'Votre Statut Actuel', creditsAvailable: 'Annonces Disponibles', currentTier: 'Niveau Actuel', freeTier: 'Gratuit', featuresUnlocked: 'Fonctionnalités Débloquées', featureAi: 'Assistance IA', featureScheduling: 'Planification', featureRecurring: 'Annonces Récurrentes', featureTreslog: 'Accès TRESLOG', featureKeepsakes: 'Souvenirs Téléchargeables', creditPacks: 'Packs de Crédits', creditPacksSubtitle: 'Ajoutez des annonces tout en gardant vos fonctionnalités', featuresStay: 'Vos fonctionnalités {tier} restent débloquées pour toujours', bundles: 'Commencer', bundlesSubtitle: 'Débloquez des fonctionnalités et ajoutez des annonces', popular: 'Plus Populaire', oneTime: 'paiement unique', get: 'Obtenir', upgradeSection: 'Voulez-vous Plus de Fonctionnalités ?', upgradeSubtitle: 'Passez au niveau supérieur pour débloquer plus', upgradeTo: 'Passer à', refundNote: 'Les annonces sont ajoutées instantanément et n\'expirent jamais. Si quelque chose ne va pas, contactez-nous.', comingSoon: 'Paiement bientôt', limited: 'limité', unlimited: 'illimité', announcements: 'annonces', bestValue: 'Meilleure Valeur',
  },
  pt: {
    pageTitle: 'Adicionar Mais Momentos', pageSubtitle: 'Continue expressando o que importa', backToDashboard: 'Voltar ao Painel', currentStatus: 'Seu Status Atual', creditsAvailable: 'Anúncios Disponíveis', currentTier: 'Nível Atual', freeTier: 'Grátis', featuresUnlocked: 'Funcionalidades Desbloqueadas', featureAi: 'Assistência de IA', featureScheduling: 'Agendamento', featureRecurring: 'Anúncios Recorrentes', featureTreslog: 'Acesso ao TRESLOG', featureKeepsakes: 'Lembranças para Download', creditPacks: 'Pacotes de Créditos', creditPacksSubtitle: 'Adicione mais anúncios mantendo todas as suas funcionalidades', featuresStay: 'Suas funcionalidades {tier} permanecem desbloqueadas para sempre', bundles: 'Começar', bundlesSubtitle: 'Desbloqueie funcionalidades e adicione anúncios', popular: 'Mais Popular', oneTime: 'pagamento único', get: 'Obter', upgradeSection: 'Quer Mais Funcionalidades?', upgradeSubtitle: 'Atualize para desbloquear mais capacidades', upgradeTo: 'Atualizar para', refundNote: 'Os anúncios são adicionados instantaneamente e nunca expiram. Se algo não parecer certo, entre em contato.', comingSoon: 'Pagamento em breve', limited: 'limitado', unlimited: 'ilimitado', announcements: 'anúncios', bestValue: 'Melhor Valor',
  },
  ar: {
    pageTitle: 'إضافة المزيد من اللحظات', pageSubtitle: 'استمر في التعبير عما يهم', backToDashboard: 'العودة للوحة', currentStatus: 'حالتك الحالية', creditsAvailable: 'إعلانات متاحة', currentTier: 'المستوى الحالي', freeTier: 'مجاني', featuresUnlocked: 'الميزات المفتوحة', featureAi: 'مساعدة الكتابة بالذكاء الاصطناعي', featureScheduling: 'الجدولة', featureRecurring: 'إعلانات متكررة', featureTreslog: 'الوصول إلى TRESLOG', featureKeepsakes: 'تذكارات قابلة للتنزيل', creditPacks: 'حزم الأرصدة', creditPacksSubtitle: 'أضف المزيد من الإعلانات مع الاحتفاظ بجميع ميزاتك', featuresStay: 'ميزات {tier} الخاصة بك تبقى مفتوحة للأبد', bundles: 'ابدأ', bundlesSubtitle: 'افتح الميزات وأضف إعلانات', popular: 'الأكثر شعبية', oneTime: 'دفعة واحدة', get: 'احصل', upgradeSection: 'تريد المزيد من الميزات؟', upgradeSubtitle: 'قم بالترقية لفتح إمكانيات إضافية', upgradeTo: 'الترقية إلى', refundNote: 'تُضاف الإعلانات فوراً ولا تنتهي أبداً. إذا كان هناك شيء غير مناسب، تواصل معنا.', comingSoon: 'الدفع قريباً', limited: 'محدود', unlimited: 'غير محدود', announcements: 'إعلانات', bestValue: 'أفضل قيمة',
  },
  ja: {
    pageTitle: 'もっと瞬間を追加', pageSubtitle: '大切なことを伝え続けましょう', backToDashboard: 'ダッシュボードに戻る', currentStatus: '現在のステータス', creditsAvailable: '利用可能なアナウンスメント', currentTier: '現在のティア', freeTier: '無料', featuresUnlocked: '解放された機能', featureAi: 'AIライティングアシスト', featureScheduling: 'スケジューリング', featureRecurring: 'リピートアナウンスメント', featureTreslog: 'TRESLOGアクセス', featureKeepsakes: 'ダウンロード可能な記念品', creditPacks: 'クレジットパック', creditPacksSubtitle: 'すべての機能を維持しながらアナウンスメントを追加', featuresStay: '{tier}の機能は永久に解放されたままです', bundles: '始める', bundlesSubtitle: '機能を解放してアナウンスメントを追加', popular: '最も人気', oneTime: '一回払い', get: '取得', upgradeSection: 'もっと機能が欲しいですか？', upgradeSubtitle: 'アップグレードして追加機能を解放', upgradeTo: 'にアップグレード', refundNote: 'アナウンスメントは即座に追加され、期限切れになりません。何かおかしいと感じたら、ご連絡ください。', comingSoon: '支払い準備中', limited: '制限あり', unlimited: '無制限', announcements: 'アナウンスメント', bestValue: 'ベストバリュー',
  },
  ko: {
    pageTitle: '더 많은 순간 추가', pageSubtitle: '중요한 것을 계속 표현하세요', backToDashboard: '대시보드로 돌아가기', currentStatus: '현재 상태', creditsAvailable: '사용 가능한 어나운스먼트', currentTier: '현재 티어', freeTier: '무료', featuresUnlocked: '해제된 기능', featureAi: 'AI 작성 지원', featureScheduling: '예약', featureRecurring: '반복 어나운스먼트', featureTreslog: 'TRESLOG 접근', featureKeepsakes: '다운로드 가능한 기념품', creditPacks: '크레딧 팩', creditPacksSubtitle: '모든 기능을 유지하면서 어나운스먼트 추가', featuresStay: '{tier} 기능은 영원히 해제된 상태로 유지됩니다', bundles: '시작하기', bundlesSubtitle: '기능을 해제하고 어나운스먼트를 추가하세요', popular: '가장 인기', oneTime: '일회성', get: '받기', upgradeSection: '더 많은 기능을 원하시나요?', upgradeSubtitle: '업그레이드하여 추가 기능 해제', upgradeTo: '로 업그레이드', refundNote: '어나운스먼트는 즉시 추가되며 만료되지 않습니다. 무언가 맞지 않으면 연락해 주세요.', comingSoon: '결제 곧 가능', limited: '제한', unlimited: '무제한', announcements: '어나운스먼트', bestValue: '최고 가치',
  },
  id: {
    pageTitle: 'Tambah Lebih Banyak Momen', pageSubtitle: 'Terus ungkapkan apa yang penting', backToDashboard: 'Kembali ke Dasbor', currentStatus: 'Status Anda Saat Ini', creditsAvailable: 'Pengumuman Tersedia', currentTier: 'Tier Saat Ini', freeTier: 'Gratis', featuresUnlocked: 'Fitur yang Dibuka', featureAi: 'Bantuan Penulisan AI', featureScheduling: 'Penjadwalan', featureRecurring: 'Pengumuman Berulang', featureTreslog: 'Akses TRESLOG', featureKeepsakes: 'Kenangan yang Dapat Diunduh', creditPacks: 'Paket Kredit', creditPacksSubtitle: 'Tambahkan pengumuman sambil mempertahankan semua fitur', featuresStay: 'Fitur {tier} Anda tetap terbuka selamanya', bundles: 'Mulai', bundlesSubtitle: 'Buka fitur dan tambahkan pengumuman', popular: 'Paling Populer', oneTime: 'sekali bayar', get: 'Dapatkan', upgradeSection: 'Ingin Lebih Banyak Fitur?', upgradeSubtitle: 'Tingkatkan untuk membuka kemampuan tambahan', upgradeTo: 'Tingkatkan ke', refundNote: 'Pengumuman ditambahkan langsung dan tidak pernah kedaluwarsa. Jika ada yang tidak beres, hubungi kami.', comingSoon: 'Pembayaran segera hadir', limited: 'terbatas', unlimited: 'tak terbatas', announcements: 'pengumuman', bestValue: 'Nilai Terbaik',
  },
};

const tierNames: Record<BundleType | 'free', Record<string, string>> = {
  free: { en: 'Free', es: 'Gratis', fr: 'Gratuit', pt: 'Grátis', ar: 'مجاني', ja: '無料', ko: '무료', id: 'Gratis' },
  starter: { en: 'Starter', es: 'Inicial', fr: 'Débutant', pt: 'Inicial', ar: 'مبتدئ', ja: 'スターター', ko: '스타터', id: 'Pemula' },
  connection: { en: 'Connection', es: 'Conexión', fr: 'Connexion', pt: 'Conexão', ar: 'اتصال', ja: 'コネクション', ko: '커넥션', id: 'Koneksi' },
  legacy: { en: 'Legacy', es: 'Legado', fr: 'Héritage', pt: 'Legado', ar: 'إرث', ja: 'レガシー', ko: '레거시', id: 'Warisan' },
};

export default function AddCredits() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { credits, isLoading, error: creditsError, refetch } = useUserCredits();
  const { isActiveFounder, slotsRemaining, founderCurrentPlan } = useFounderStatus();
  const founder = useFounderOffer();
  const [isProcessing, setIsProcessing] = useState(false);

  const t = copy[language] || copy.en;

  const hasPurchasedBundle = credits?.has_purchased_bundle ?? false;
  const currentTier = credits?.highest_bundle_purchased ?? null;
  const tierKey = currentTier ?? 'free';

  // Determine available upgrade options for non-legacy users
  const getUpgradeOptions = () => {
    if (!currentTier || currentTier === 'legacy') return [];
    const tierOrder: BundleType[] = ['starter', 'connection', 'legacy'];
    const currentIndex = tierOrder.indexOf(currentTier);
    return BUNDLE_CONFIGS.filter((_, index) => index > currentIndex);
  };

  const upgradeOptions = getUpgradeOptions();

  const handlePurchasePack = async (packId: CreditPackType) => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { itemId: packId, source: 'add-credits-pack' },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error('Could not start checkout. Please try again.');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseBundle = async (bundleId: BundleType) => {
    setIsProcessing(true);
    try {
      // Use founder pricing for connection bundle when founder spots are still available
      // This applies to ALL users (not just existing founders) so new users get the founder price
      const itemId = bundleId === 'connection' && !founder.isLoading && founder.phase !== 'regular'
        ? 'connection_founder'
        : bundleId;
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { itemId, source: 'add-credits-bundle' },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error('Could not start checkout. Please try again.');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Feature access based on tier
  const getFeatureAccess = () => {
    const features = [
      { 
        key: 'ai', 
        label: t.featureAi, 
        icon: Sparkles,
        unlocked: hasPurchasedBundle,
        status: hasPurchasedBundle ? t.unlimited : t.limited
      },
      { 
        key: 'scheduling', 
        label: t.featureScheduling, 
        icon: Calendar,
        unlocked: currentTier === 'connection' || currentTier === 'legacy',
        status: currentTier === 'connection' || currentTier === 'legacy' ? '✓' : '—'
      },
      { 
        key: 'recurring', 
        label: t.featureRecurring, 
        icon: Zap,
        unlocked: currentTier === 'legacy',
        status: currentTier === 'legacy' ? '✓' : '—'
      },
      { 
        key: 'treslog', 
        label: t.featureTreslog, 
        icon: Feather,
        unlocked: currentTier === 'legacy',
        status: currentTier === 'legacy' ? '✓' : '—'
      },
      { 
        key: 'keepsakes', 
        label: t.featureKeepsakes, 
        icon: Download,
        unlocked: currentTier === 'legacy',
        status: currentTier === 'legacy' ? '✓' : '—'
      },
    ];
    return features;
  };

  const features = getFeatureAccess();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-hero-gradient">
        <AuthenticatedHeader />
        <main className="container py-8 max-w-4xl">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-40 bg-muted rounded" />
            <div className="grid gap-4 md:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-48 bg-muted rounded" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Never render "Free / 0" as a fallback for a signed-in user when the
  // credits fetch failed — that misrepresents their actual tier and balance.
  if (creditsError || !credits) {
    return (
      <div className="min-h-screen bg-hero-gradient">
        <AuthenticatedHeader />
        <main className="container py-16 max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Couldn't load your account</h1>
          <p className="text-muted-foreground">
            We couldn't reach your account details just now. Your credits and tier are safe — please retry.
          </p>
          <Button onClick={() => refetch()}>Try again</Button>
          <div>
            <Link to="/dashboard" className="text-sm text-muted-foreground underline">
              Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <AuthenticatedHeader />

      <main className="container py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t.pageTitle}</h1>
              <p className="text-muted-foreground">{t.pageSubtitle}</p>
            </div>
          </div>

          {/* Current Status Card */}
          <Card className="bg-gradient-to-br from-primary/5 via-background to-accent/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {t.currentStatus}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap gap-6">
                {/* Credits */}
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-full bg-primary/10">
                    <Heart className="h-5 w-5 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{credits?.announcement_credits ?? 0}</p>
                    <p className="text-sm text-muted-foreground">{t.creditsAvailable}</p>
                  </div>
                </div>

                {/* Current Tier */}
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-full ${currentTier ? bundleColors[currentTier].bg : 'bg-muted'}`}>
                    {currentTier ? (
                      (() => {
                        const Icon = bundleIcons[currentTier];
                        return <Icon className={`h-5 w-5 ${bundleColors[currentTier].text}`} />;
                      })()
                    ) : (
                      <Package className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="text-lg font-semibold">
                      {tierNames[tierKey][language] || tierNames[tierKey].en}
                    </p>
                    <p className="text-sm text-muted-foreground">{t.currentTier}</p>
                  </div>
                </div>
              </div>

              {/* Features Status */}
              {hasPurchasedBundle && (
                <div className="pt-4 border-t border-border/50">
                  <p className="text-sm font-medium text-muted-foreground mb-3">{t.featuresUnlocked}</p>
                  <div className="flex flex-wrap gap-2">
                    {features.map((feature) => (
                      <Badge 
                        key={feature.key}
                        variant={feature.unlocked ? 'default' : 'secondary'}
                        className={feature.unlocked ? 'bg-primary/10 text-primary border-primary/20' : 'opacity-50'}
                      >
                        <feature.icon className="h-3 w-3 mr-1" />
                        {feature.label}
                        <span className="ml-1 text-xs opacity-70">({feature.status})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Founder Plan Transition - upgrade/downgrade for active founders */}
          {isActiveFounder && <FounderPlanTransition />}

          {/* Conditional: Credit Packs for existing bundle purchasers */}
          {hasPurchasedBundle ? (
            <div className="space-y-6">
              {/* Credit Packs Section */}
              <div className="text-center">
                <h2 className="text-2xl font-bold">{t.creditPacks}</h2>
                <p className="text-muted-foreground">{t.creditPacksSubtitle}</p>
                <p className="text-sm text-primary mt-2 flex items-center justify-center gap-1">
                  <Shield className="h-4 w-4" />
                  {t.featuresStay.replace('{tier}', tierNames[tierKey][language] || tierNames[tierKey].en)}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {CREDIT_PACK_CONFIGS.map((pack, index) => (
                  <motion.div
                    key={pack.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card className={`h-full ${pack.featured ? 'border-primary shadow-lg' : ''}`}>
                      {pack.featured && (
                          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                            {t.bestValue || 'Best Value'}
                        </Badge>
                      )}
                      <CardHeader className="text-center pb-2">
                        <div className="mx-auto mb-2 p-3 rounded-full bg-primary/10 w-fit">
                          <Heart className="h-6 w-6 text-primary" fill="currentColor" />
                        </div>
                        <CardTitle className="text-lg">{pack.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{pack.description}</p>
                      </CardHeader>
                      <CardContent className="text-center space-y-4">
                        <div>
                          <span className="text-3xl font-bold text-primary">+{pack.credits}</span>
                          <p className="text-sm text-muted-foreground">{t.announcements}</p>
                        </div>
                        <div>
                          <span className="text-2xl font-bold">{pack.priceFormatted}</span>
                        </div>
                        <Button
                          className="w-full"
                          variant={pack.featured ? 'default' : 'outline'}
                          onClick={() => handlePurchasePack(pack.id)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? '...' : `${t.get} ${pack.name}`}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Upgrade Section for non-Legacy users */}
              {upgradeOptions.length > 0 && (
                <div className="pt-8 border-t border-border/50 space-y-4">
                  <div className="text-center">
                    <h3 className="text-xl font-semibold">{t.upgradeSection}</h3>
                    <p className="text-sm text-muted-foreground">{t.upgradeSubtitle}</p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    {upgradeOptions.map((bundle) => {
                      const Icon = bundleIcons[bundle.id];
                      const colors = bundleColors[bundle.id];
                      return (
                        <Card key={bundle.id} className="relative">
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${colors.bg}`}>
                                <Icon className={`h-5 w-5 ${colors.text}`} />
                              </div>
                              <div>
                                <CardTitle className="text-lg">{bundle.name}</CardTitle>
                                <p className="text-sm text-muted-foreground">{bundle.tagline}</p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <ul className="space-y-1">
                              {bundle.features.slice(0, 3).map((feature, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                  <span className="text-muted-foreground">{feature}</span>
                                </li>
                              ))}
                            </ul>
                            {bundle.id === 'connection' && !founder.isLoading && founder.phase !== 'regular' ? (
                              <div className="space-y-2">
                                <p className="text-xs text-primary font-medium">
                                  {founder.phase === 'phase1'
                                    ? `Founder Phase 1 — ${founder.spotsLeft} spots left at $${founder.price}`
                                    : `Founder Phase 2 — ${founder.spotsLeft} spots remaining at $${founder.price}`}
                                </p>
                                <Button
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => handlePurchaseBundle(bundle.id)}
                                  disabled={isProcessing}
                                >
                                  {t.upgradeTo} {bundle.name} — <span className="line-through opacity-60 mr-1">{bundle.priceFormatted}</span>${founder.price}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => handlePurchaseBundle(bundle.id)}
                                disabled={isProcessing}
                              >
                                {t.upgradeTo} {bundle.name} — {bundle.priceFormatted}
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Bundle Options for users without any bundle */
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold">{t.bundles}</h2>
                <p className="text-muted-foreground">{t.bundlesSubtitle}</p>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {BUNDLE_CONFIGS.map((bundle, index) => {
                  const Icon = bundleIcons[bundle.id];
                  const colors = bundleColors[bundle.id];
                  return (
                    <motion.div
                      key={bundle.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Card className={`relative h-full ${bundle.featured ? 'border-primary shadow-lg' : ''}`}>
                        {bundle.featured && (
                          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                            {t.popular}
                          </Badge>
                        )}
                        <CardHeader className="text-center pb-4">
                          <div className={`mx-auto mb-3 p-3 rounded-full ${colors.bg} w-fit`}>
                            <Icon className={`h-6 w-6 ${colors.text}`} />
                          </div>
                          <CardTitle className="text-xl">{bundle.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{bundle.tagline}</p>
                          <div className="mt-2">
                            {bundle.id === 'connection' && !founder.isLoading && founder.phase !== 'regular' ? (
                              <>
                                <span className="text-3xl font-bold">${founder.price}</span>
                                <span className="text-lg text-muted-foreground line-through ml-2">{bundle.priceFormatted}</span>
                                <span className="text-muted-foreground text-sm"> {t.oneTime}</span>
                                <p className="text-xs text-primary mt-1 font-medium">
                                  {founder.phase === 'phase1'
                                    ? `Founder Phase 1 — ${founder.spotsLeft} spots left at $${founder.price}`
                                    : `Founder Phase 2 — ${founder.spotsLeft} spots remaining at $${founder.price}`}
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {founder.phase === 'phase1' ? 'Lock in the lowest lifetime rate.' : 'Founder pricing ends when this fills.'}
                                </p>
                              </>
                            ) : (
                              <>
                                <span className="text-3xl font-bold">{bundle.priceFormatted}</span>
                                <span className="text-muted-foreground text-sm"> {t.oneTime}</span>
                              </>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <ul className="space-y-2">
                            {bundle.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-start gap-2 text-sm">
                                <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                <span className="text-muted-foreground">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <Button
                            className="w-full"
                            variant={bundle.featured ? 'default' : 'outline'}
                            onClick={() => handlePurchaseBundle(bundle.id)}
                            disabled={isProcessing}
                          >
                            {isProcessing ? '...' : `${t.get} ${bundle.name}`}
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Refund Note */}
          <p className="text-center text-sm text-muted-foreground pt-4">
            {t.refundNote}
          </p>
        </motion.div>
      </main>
    </div>
  );
}
